import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useEntityFinderContext } from "../context/EntityFinderContext";
import { Contact } from "@/bryntum-lib/models/Contact";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { getFontSize } from "@/react/context/bryntumContext";

export const ContactTab = () => {
  const { data, handleClose } = useEntityFinderContext();
  const { editorHandler } = useSwivuBryntumContext();
  const contacts = data as Contact[];
  const containerRef = useRef<HTMLDivElement>(null);

  const gap = 12;
  const rowVirtualizer = useVirtualizer({
    count: contacts.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => getFontSize() * 3 + gap,
    overscan: 8
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const handleContactClick = (contact: Contact) => {
    editorHandler.openEditor(
      EDITOR_KEYS.contactEditor,
      { id: contact.id },
      { contact },
      { by: "recordId", useExisting: true }
    );
    handleClose();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={containerRef} className="flex-1 overflow-auto bg-background">
        {contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
            <svg className="w-12 h-12 mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <p className="text-sm">No contacts found</p>
          </div>
        ) : (
          <div className="relative w-full" style={{ height: `${totalSize}px` }}>
            {virtualItems.map((virtualRow) => {
              const contact = contacts[virtualRow.index];
              return (
                <div
                  key={contact.id}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <div
                    className="px-4 flex items-center bg-card hover:opacity-90 cursor-pointer transition-all rounded-md"
                    style={{ height: `${virtualRow.size - gap}px`, marginBottom: `${gap}px` }}
                    onClick={() => handleContactClick(contact)}
                  >
                    <div className="font-medium text-foreground line-clamp-1">{contact.name || "N/A"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
